// Phase 17 — shareable curated symbol packs.
//
// A pack is encoded entirely into the URL — no backend, no storage. The encoded
// form is base64url of a compact JSON payload that lists each symbol by its
// codepoint sequence (so the real characters, slugs and names are all
// recoverable on the receiving end via the generated index). decode(encode(x))
// round-trips for arbitrarily large sets; the same logic is mirrored in
// scripts/validate.mjs as the Phase 17 gate.

import { symbolSlug } from "@/data/symbols";
import { getSymbolName } from "@/data/symbol-names";

export const PACK_VERSION = 1;

export interface Pack {
  title: string;
  /** Raw symbol strings, in order. */
  symbols: string[];
}

export interface PackItem {
  symbol: string;
  slug: string;
  name: string;
}

// --- codepoint sequence <-> symbol -----------------------------------------
function toHexSeq(symbol: string): string {
  return Array.from(symbol)
    .map((c) => c.codePointAt(0)!.toString(16))
    .join("-");
}

function fromHexSeq(seq: string): string | null {
  const cps = seq.split("-").map((h) => parseInt(h, 16));
  if (cps.some((cp) => !Number.isInteger(cp) || cp < 0 || cp > 0x10ffff)) return null;
  try {
    return String.fromCodePoint(...cps);
  } catch {
    return null;
  }
}

// --- env-safe base64url -----------------------------------------------------
function bytesToB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 =
    typeof btoa === "function" ? btoa(bin) : Buffer.from(bin, "binary").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin =
    typeof atob === "function" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// --- public API -------------------------------------------------------------
/** Encode a pack into a URL-path-safe token (chars: A–Z a–z 0–9 - _). */
export function encodePack(pack: Pack): string {
  const payload = {
    v: PACK_VERSION,
    t: pack.title ? pack.title.slice(0, 120) : undefined,
    s: pack.symbols.map(toHexSeq),
  };
  return bytesToB64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

/** Decode a pack token. Returns null for anything malformed or empty. */
export function decodePack(token: string): Pack | null {
  try {
    const json = new TextDecoder().decode(b64UrlToBytes(token));
    const obj = JSON.parse(json) as { v?: unknown; t?: unknown; s?: unknown };
    if (!obj || obj.v !== PACK_VERSION || !Array.isArray(obj.s)) return null;
    const symbols: string[] = [];
    for (const entry of obj.s) {
      if (typeof entry !== "string") continue;
      const sym = fromHexSeq(entry);
      if (sym) symbols.push(sym);
    }
    if (symbols.length === 0) return null;
    return { title: typeof obj.t === "string" ? obj.t : "", symbols };
  } catch {
    return null;
  }
}

/** Resolve a pack's symbols to slug + name for rendering, de-duped by symbol. */
export function resolvePackItems(symbols: string[]): PackItem[] {
  const seen = new Set<string>();
  const out: PackItem[] = [];
  for (const symbol of symbols) {
    if (seen.has(symbol)) continue;
    seen.add(symbol);
    out.push({ symbol, slug: symbolSlug(symbol), name: getSymbolName(symbol) });
  }
  return out;
}
